import { FC, useMemo, useState } from 'react';

import { keyBy, uniqBy } from 'lodash';
import moment from 'moment';

import { SparkSnap } from '@sparkplug/lib';

import SparkSnapIcon from '@features/spark-snaps/components/SparkSnapIcon';
import { useSnapViewQueue } from '@features/spark-snaps/hooks/useSnapViewQueue';
import { useCreateSnapTemplateMutation } from '@features/spark-snaps/mutations';
import { useAccountSparkSnapsQuery, useSnapTemplatesQuery } from '@features/spark-snaps/queries';

import Dropdown from '@components/dropdown/Dropdown';
import { MoreVert as MoreIcon } from '@components/icons';
import Table from '@components/table/Table';
import Toolbar from '@components/toolbar/Toolbar';

import { useSparkplugAccount } from '@hooks/SparkplugAccountsHooks';
import { useSearch } from '@hooks/UIHooks';

import { openUrl } from '@helpers/ui';

import { THeadCell } from '@app/types/TableTypes';
import { IOption } from '@app/types/UITypes';

import './SparkSnapsDashboardView.scss';

export const useSnapsDashboardHeadCells = ({
    accountId,
}: {
    accountId: string;
}): { headCells: THeadCell<SparkSnap>[]; SnapViewer: FC<{}> } => {
    const { handleViewSnap, SnapViewer } = useSnapViewQueue({ accountId });
    const { isCreatingSnapTemplate, createSnapTemplateAsync } = useCreateSnapTemplateMutation({
        accountId,
    });

    const handleSaveAsTemplate = async ({ snapId }: { snapId: number }) => {
        if (snapId && !isCreatingSnapTemplate) {
            await createSnapTemplateAsync({ snapId });
        }
    };

    const handleOpenSpark = (sparkId: string) => {
        openUrl(`/${accountId}/sparks/${sparkId}`);
    };

    const handlePreview = ({ snapId }: { snapId: number }) => {
        handleViewSnap(snapId);
    };

    const headCells: THeadCell<SparkSnap>[] = [
        {
            id: 'name',
            label: 'Snap Name',
            sortType: 'string',
            render: (row) => (
                <Table.Cell
                    className="table-cell_snap-name"
                    onClick={() => handlePreview({ snapId: row.id })}
                >
                    <div>
                        <SparkSnapIcon imgUrl={row.posterImg} title={row.name} />
                        <span>{row.name}</span>
                    </div>
                </Table.Cell>
            ),
        },
        {
            id: 'spark.sparkBrand.name',
            label: 'Brand',
            sortType: 'string',
            render: (row) => <Table.Cell>{row.spark.sparkBrand.name}</Table.Cell>,
        },
        {
            id: 'spark.name',
            label: 'Spark Name',
            sortType: 'string',
            render: (row) => (
                <Table.Cell onClick={() => handleOpenSpark(row.spark._id)}>
                    {row.spark.name}
                </Table.Cell>
            ),
        },
        {
            id: 'spark.retailer.name',
            label: 'Retailer',
            sortType: 'string',
            render: (row) => <Table.Cell>{row.spark.retailer.name}</Table.Cell>,
        },
        {
            id: 'spark.startDate',
            label: 'Start Date',
            sortType: 'date',
            render: (row) => (
                <Table.Cell>{moment(row.spark.startDate).format('MM/DD/YYYY')}</Table.Cell>
            ),
        },
        {
            id: 'spark.endDate',
            label: 'End Date',
            sortType: 'date',
            render: (row) => (
                <Table.Cell>{moment(row.spark.endDate).format('MM/DD/YYYY')}</Table.Cell>
            ),
        },
        {
            id: 'viewCount',
            label: 'Views',
            isHidden: true,
            sortType: 'numeric',
            render: (row) => <Table.Cell>{row.viewCount ?? '--'}</Table.Cell>,
        },
        {
            id: 'avgReadTime',
            label: 'Avg Read Time',
            isHidden: true,
            sortType: 'numeric',
            render: (row) => <Table.Cell>{row.avgReadTime ?? '--'}</Table.Cell>,
        },
        {
            id: 'actions',
            label: '',
            render: (row) => (
                <Table.Cell>
                    <Dropdown>
                        <Dropdown.IconButton color="neutral">
                            <MoreIcon />
                        </Dropdown.IconButton>

                        <Dropdown.Menu>
                            <Dropdown.MenuItem onClick={() => handlePreview({ snapId: row.id })}>
                                Preview Snap
                            </Dropdown.MenuItem>
                            <Dropdown.MenuItem onClick={() => handleOpenSpark(row.spark._id)}>
                                Open Spark in New Tab
                            </Dropdown.MenuItem>
                        </Dropdown.Menu>
                    </Dropdown>
                </Table.Cell>
            ),
        },
    ];

    return { headCells, SnapViewer };
};

const useSnapDashboardFilters = ({
    templateOptions,
    retailerOptions,
}: {
    templateOptions: IOption[];
    retailerOptions: IOption[];
}) => {
    const [selectedRetailers, setSelectedRetailers] = useState<IOption[]>(retailerOptions);
    const applyRetailerFilter = (snaps: SparkSnap[]) => {
        if (!selectedRetailers.length) {
            return snaps;
        }

        const selectedRetailersByValue = keyBy(selectedRetailers, 'value');

        return snaps.filter((snap) => selectedRetailersByValue[snap.spark.retailerAccountId]);
    };

    const [selectedTemplates, setSelectedTemplates] = useState<IOption[]>(templateOptions);
    const applyTemplateFilter = (snaps: SparkSnap[]) => {
        if (!selectedTemplates.length) {
            return snaps;
        }

        // TODO: We currently have no way to know which snaps were created from which templates
        // but this is mocked in the designs so the filter is just stubbed out for now
        return snaps;
    };

    return {
        selectedRetailers,
        setSelectedRetailers,
        applyRetailerFilter,
        selectedTemplates,
        setSelectedTemplates,
        applyTemplateFilter,
    };
};

interface SparkSnapsDashboardViewProps {
    accountId: string;
}

export const SparkSnapsDashboardView: FC<SparkSnapsDashboardViewProps> = ({ accountId }) => {
    const { searchFilter, onChangeSearchFilter, applySearch } = useSearch(['name']);
    const { accountSparkSnapsAreReady, accountSparkSnaps = [] } = useAccountSparkSnapsQuery({
        accountId,
    });
    const { snapTemplatesAreReady, snapTemplates = [] } = useSnapTemplatesQuery({ accountId });

    const { headCells, SnapViewer } = useSnapsDashboardHeadCells({ accountId });

    const retailerOptions = useMemo(() => {
        return uniqBy(
            accountSparkSnaps.map(({ spark }) => ({
                value: spark.retailerAccountId,
                label: spark.retailer.name,
            })),
            'value',
        );
    }, [accountSparkSnaps]);

    const templateOptions = useMemo(() => {
        return uniqBy(
            snapTemplates.map((snapTemplate) => ({
                value: String(snapTemplate.id),
                label: snapTemplate.name,
            })),
            'value',
        );
    }, [accountSparkSnaps, snapTemplates]);

    const {
        selectedRetailers,
        setSelectedRetailers,
        applyRetailerFilter,
        selectedTemplates,
        setSelectedTemplates,
        applyTemplateFilter,
    } = useSnapDashboardFilters({ templateOptions, retailerOptions });

    const rows = accountSparkSnaps.map((snap) => ({ ...snap, key: String(snap.id) }));
    const isAllRetailers =
        !selectedRetailers.length || selectedRetailers.length === retailerOptions.length;
    const isAllTemplates =
        !selectedTemplates.length || selectedTemplates.length === templateOptions.length;

    return (
        <>
            <Toolbar justifyContentStart>
                <Toolbar.Search
                    name="search"
                    defaultValue={searchFilter}
                    onChange={onChangeSearchFilter}
                />
                <Toolbar.DropdownListSelector
                    label={`${isAllRetailers ? 'All ' : ''}Retailers`}
                    options={retailerOptions}
                    selected={selectedRetailers.length ? selectedRetailers : retailerOptions}
                    onSelectionChanged={setSelectedRetailers}
                    clear={{
                        active: !isAllRetailers,
                        onClear: () => {
                            setSelectedRetailers([]);
                        },
                    }}
                />
            </Toolbar>

            <Table
                isLoading={!accountSparkSnapsAreReady}
                filters={[applyRetailerFilter, applyTemplateFilter, applySearch]}
                rows={rows}
                headCells={headCells}
                variant="raised"
            >
                <Table.RenderHead />
                <Table.RenderBody />
            </Table>

            <SnapViewer />
        </>
    );
};

export default () => {
    const { account } = useSparkplugAccount();

    if (!account) {
        return <></>;
    }

    return <SparkSnapsDashboardView accountId={account._id} />;
};
